import React from "react";
import { alpha3ToAlpha2, getName as getCountryDisplayName, registerLocale } from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import countryFlagColors from "country-flag-colors";
import type { Polygon as GeoPolygon, MultiPolygon as GeoMultiPolygon, FeatureCollection as GeoFeatureCollection } from "geojson";

registerLocale(enLocale);
import {
  Globe,
  SquareArrowOutUpRight,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calendar,
  Clock,
  FileText,
  LayoutGrid,
  List,
  Cpu,
  Workflow,
} from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import { formatVersion } from "../../utils/formatters";
import { accentTextVariants } from "../../utils/cloudBrands";
import { StatusBadge } from "./StatusBadge";
import { RBACAction } from "./RBACButton";
import type { ModuleType } from "../../constants/rbac";
import { Tip } from "./ui/tooltip";

interface Jurisdiction {
  code: string;
  label: string;
}

const KNOWN_JURISDICTION_LABELS: Record<string, string> = {
  SG: "Singapore",
  EU: "European Union",
  US: "United States",
  GB: "United Kingdom",
  JP: "Japan",
};

const DEFAULT_JURISDICTION_RGB = "244,63,94";

const SPECIAL_JURISDICTION_RGB: Record<string, string> = {
  EU: "0,51,153", // Europe flag blue
};

function normalizeCountryName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.,'’]/g, "")
    .replace(
      /\b(the|republic of|federated states of|islamic republic of|people'?s republic of|state of|province of china|democratic|socialist|kingdom of|principality of|plurinational state of|bolivarian republic of|co-operative republic of|independent state of|duchy of|sultanate of|grand duchy of|commonwealth of|union of|arab republic of|of america|dutch part|french part)\b/g,
      "",
    )
    .replace(/\s+/g, " ")
    .trim();
}

const FLAG_COLORS_BY_NORMALIZED_NAME = new Map<string, string[]>(
  countryFlagColors.map((c) => [normalizeCountryName(c.name), c.colors]),
);

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function saturationAndLightness(r: number, g: number, b: number): [number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const s = max === min ? 0 : l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
  return [s, l];
}

function pickAccentRgb(hexColors: string[]): string | null {
  let best: [number, number, number] | null = null;
  let bestSaturation = -1;
  for (const hex of hexColors) {
    const [r, g, b] = hexToRgb(hex);
    const [s, l] = saturationAndLightness(r, g, b);
    if (l < 0.12 || l > 0.88) continue;
    if (s > bestSaturation) {
      bestSaturation = s;
      best = [r, g, b];
    }
  }
  return best ? best.join(",") : null;
}

const jurisdictionAccentCache = new Map<string, string>();

function getJurisdictionAccentRgb(code: string): string {
  const cached = jurisdictionAccentCache.get(code);
  if (cached) return cached;

  let rgb = SPECIAL_JURISDICTION_RGB[code];
  if (!rgb) {
    const name = getCountryDisplayName(code, "en");
    const colors = name && FLAG_COLORS_BY_NORMALIZED_NAME.get(normalizeCountryName(name));
    rgb = (colors && pickAccentRgb(colors)) || DEFAULT_JURISDICTION_RGB;
  }
  jurisdictionAccentCache.set(code, rgb);
  return rgb;
}

function resolveCatalogJurisdiction(
  countryCode?: string | null,
  countryName?: string | null,
): Jurisdiction {
  const code = (countryCode || "").trim().toUpperCase();
  if (!code) return { code: "INTL", label: "International" };
  return { code, label: countryName?.trim() || KNOWN_JURISDICTION_LABELS[code] || code };
}

function isRealJurisdiction(code: string): boolean {
  return code in SPECIAL_JURISDICTION_RGB || Boolean(getCountryDisplayName(code, "en"));
}

export function catalogFlagAccentRgb(
  countryCode?: string | null,
  countryName?: string | null,
): string | undefined {
  const jur = resolveCatalogJurisdiction(countryCode, countryName);
  return isRealJurisdiction(jur.code) ? getJurisdictionAccentRgb(jur.code) : undefined;
}

type CountryGeometry = GeoPolygon | GeoMultiPolygon;
let countryGeometriesPromise: Promise<Map<string, CountryGeometry>> | null = null;

export function preloadCountryGeometries(): void {
  void loadCountryGeometries();
}

function loadCountryGeometries(): Promise<Map<string, CountryGeometry>> {
  if (!countryGeometriesPromise) {
    countryGeometriesPromise = import("@geo-maps/countries-land-1km/map.geo.json")
      .then((mod) => {
        const collection = (mod as unknown as { default: GeoFeatureCollection }).default
          ?? (mod as unknown as GeoFeatureCollection);
        const byAlpha2 = new Map<string, CountryGeometry>();
        for (const f of collection.features) {
          const a3 = (f.properties as { A3?: string } | null)?.A3;
          const alpha2 = a3 && alpha3ToAlpha2(a3);
          if (!alpha2 || !f.geometry) continue;
          const geometry = f.geometry as CountryGeometry;

          const existing = byAlpha2.get(alpha2);
          if (!existing || geometryArea(geometry) > geometryArea(existing)) {
            byAlpha2.set(alpha2, geometry);
          }
        }
        return byAlpha2;
      })
      .catch((err) => {

        console.error("Failed to load country geometries", err);
        countryGeometriesPromise = null;
        return new Map<string, CountryGeometry>();
      });
  }
  return countryGeometriesPromise;
}

function ringArea(ring: number[][]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
}

function geometryArea(geometry: CountryGeometry): number {
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  return polygons.reduce((sum, rings) => sum + ringArea(rings[0]), 0);
}

function perpendicularDistance(p: number[], a: number[], b: number[]): number {
  const [x, y] = p;
  const [x1, y1] = a;
  const [x2, y2] = b;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(x - x1, y - y1);
  const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / lenSq));
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
}

function simplifyRing(points: number[][], epsilon: number): number[][] {
  if (points.length < 4) return points;
  const last = points.length - 1;
  let maxDist = 0;
  let index = 0;
  for (let i = 1; i < last; i++) {
    const dist = perpendicularDistance(points[i], points[0], points[last]);
    if (dist > maxDist) {
      maxDist = dist;
      index = i;
    }
  }
  if (maxDist > epsilon) {
    const left = simplifyRing(points.slice(0, index + 1), epsilon);
    const right = simplifyRing(points.slice(index), epsilon);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[last]];
}

function selectVisibleRings(geometry: CountryGeometry): number[][][] {
  if (geometry.type === "Polygon") return [geometry.coordinates[0]];

  const polygons = geometry.coordinates;
  let mainIndex = 0;
  let mainArea = -1;
  polygons.forEach((poly, i) => {
    const area = ringArea(poly[0]);
    if (area > mainArea) {
      mainArea = area;
      mainIndex = i;
    }
  });
  return [polygons[mainIndex][0]];
}

function geometryToPath(
  geometry: CountryGeometry,
): { d: string; bbox: { x: number; y: number; width: number; height: number } } {
  const visibleRings = selectVisibleRings(geometry);

  const exteriorLats = visibleRings[0].map(([, lat]) => lat);
  const centerLat = (Math.min(...exteriorLats) + Math.max(...exteriorLats)) / 2;
  const lonScale = Math.cos((centerLat * Math.PI) / 180) || 1;

  const projectedRings = visibleRings.map((ring) => ring.map(([lon, lat]) => [lon * lonScale, -lat]));

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const ring of projectedRings) {
    for (const [x, y] of ring) {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  const epsilon = Math.max(maxX - minX, maxY - minY) * 0.006;

  const parts = projectedRings.map((ring) => {
    const simplified = simplifyRing(ring, epsilon);
    return simplified.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ") + " Z";
  });

  return { d: parts.join(" "), bbox: { x: minX, y: minY, width: maxX - minX, height: maxY - minY } };
}

export interface StatusAccent {
  bar: string;
  bg: string;
  border: string;
  hoverBorder: string;
  hoverGlow: string;
  sheenVia: string;

  glowRgb: string;
}

export function statusAccent(status: string): StatusAccent {
  switch (status) {
    case "published":
      return {
        bar: "bg-emerald-500",
        bg: "bg-gradient-to-br from-emerald-100 via-emerald-50/70 to-card dark:from-emerald-500/35 dark:via-emerald-500/10 dark:to-card",
        border: "border-emerald-200 dark:border-emerald-500/50",
        hoverBorder: "hover:border-emerald-400 dark:hover:border-emerald-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(16,185,129,0.32)] dark:hover:shadow-[0_0_28px_rgba(16,185,129,0.36)]",
        sheenVia: "via-emerald-400/15 dark:via-emerald-400/20",
        glowRgb: "5,150,105",
      };
    case "approved":
      return {
        bar: "bg-violet-500",
        bg: "bg-gradient-to-br from-violet-100 via-violet-50/70 to-card dark:from-violet-500/35 dark:via-violet-500/10 dark:to-card",
        border: "border-violet-200 dark:border-violet-500/50",
        hoverBorder: "hover:border-violet-400 dark:hover:border-violet-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(139,92,246,0.32)] dark:hover:shadow-[0_0_28px_rgba(139,92,246,0.36)]",
        sheenVia: "via-violet-400/15 dark:via-violet-400/20",
        glowRgb: "139,92,246",
      };
    case "in_review":
      return {
        bar: "bg-blue-500",
        bg: "bg-gradient-to-br from-blue-100 via-blue-50/70 to-card dark:from-blue-500/35 dark:via-blue-500/10 dark:to-card",
        border: "border-blue-200 dark:border-blue-500/50",
        hoverBorder: "hover:border-blue-400 dark:hover:border-blue-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(59,130,246,0.32)] dark:hover:shadow-[0_0_28px_rgba(59,130,246,0.36)]",
        sheenVia: "via-blue-400/15 dark:via-blue-400/20",
        glowRgb: "59,130,246",
      };
    case "in_progress":
      return {
        bar: "bg-amber-500",
        bg: "bg-gradient-to-br from-amber-100 via-amber-50/70 to-card dark:from-amber-500/35 dark:via-amber-500/10 dark:to-card",
        border: "border-amber-200 dark:border-amber-500/50",
        hoverBorder: "hover:border-amber-400 dark:hover:border-amber-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(245,158,11,0.32)] dark:hover:shadow-[0_0_28px_rgba(245,158,11,0.36)]",
        sheenVia: "via-amber-400/15 dark:via-amber-400/20",
        glowRgb: "245,158,11",
      };
    case "rejected":
      return {
        bar: "bg-red-500",
        bg: "bg-gradient-to-br from-red-100 via-red-50/70 to-card dark:from-red-500/35 dark:via-red-500/10 dark:to-card",
        border: "border-red-200 dark:border-red-500/50",
        hoverBorder: "hover:border-red-400 dark:hover:border-red-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(239,68,68,0.32)] dark:hover:shadow-[0_0_28px_rgba(239,68,68,0.36)]",
        sheenVia: "via-red-400/15 dark:via-red-400/20",
        glowRgb: "239,68,68",
      };
    case "superseded":
      return {
        bar: "bg-orange-500",
        bg: "bg-gradient-to-br from-orange-100 via-orange-50/70 to-card dark:from-orange-500/35 dark:via-orange-500/10 dark:to-card",
        border: "border-orange-200 dark:border-orange-500/50",
        hoverBorder: "hover:border-orange-400 dark:hover:border-orange-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(249,115,22,0.32)] dark:hover:shadow-[0_0_28px_rgba(249,115,22,0.36)]",
        sheenVia: "via-orange-400/15 dark:via-orange-400/20",
        glowRgb: "249,115,22",
      };
    case "withdrawn":
    case "retired":
      return {
        bar: "bg-rose-600",
        bg: "bg-gradient-to-br from-rose-100 via-rose-50/70 to-card dark:from-rose-500/35 dark:via-rose-500/10 dark:to-card",
        border: "border-rose-200 dark:border-rose-500/50",
        hoverBorder: "hover:border-rose-400 dark:hover:border-rose-500/60",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(225,29,72,0.32)] dark:hover:shadow-[0_0_28px_rgba(225,29,72,0.36)]",
        sheenVia: "via-rose-400/15 dark:via-rose-400/20",
        glowRgb: "225,29,72",
      };
    default: // draft
      return {
        bar: "bg-slate-300 dark:bg-white/25",
        bg: "bg-gradient-to-br from-slate-100 via-slate-50/70 to-card dark:from-slate-500/30 dark:via-slate-500/10 dark:to-card",
        border: "border-slate-200 dark:border-slate-500/50",
        hoverBorder: "hover:border-slate-400 dark:hover:border-white/35",
        hoverGlow:
          "hover:shadow-[0_12px_28px_-8px_rgba(100,116,139,0.30)] dark:hover:shadow-[0_0_28px_rgba(148,163,184,0.34)]",
        sheenVia: "via-slate-400/15 dark:via-slate-300/20",
        glowRgb: "71,85,105",
      };
  }
}

export function StatusGlow({ accent }: { accent: { glowRgb: string } }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -top-16 -right-16 size-56 rounded-full blur-3xl opacity-70 dark:opacity-60"
      style={{
        background: `radial-gradient(circle, rgba(${accent.glowRgb},0.6), transparent 70%)`,
      }}
    />
  );
}

export function StatusBar({ accent }: { accent: { glowRgb: string } }) {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute -top-10 -left-10 size-48 z-0"
        style={{
          background: `radial-gradient(circle at top left, rgba(${accent.glowRgb},0.55), rgba(${accent.glowRgb},0.12) 45%, transparent 70%)`,
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 w-1.5 z-[1]"
        style={{
          background: `linear-gradient(180deg, rgba(${accent.glowRgb},1) 0%, rgba(${accent.glowRgb},0.55) 55%, rgba(${accent.glowRgb},0.08) 100%)`,
        }}
      />
    </>
  );
}

export type ModuleColor =
  | "emerald"
  | "sky"
  | "violet"
  | "rose"
  | "indigo"
  | "amber"
  | "teal"
  | "slate";

export interface ModuleTint {
  iconBg: string;
  iconRing: string;
  iconText: string;
  watermark: string;
}

const MODULE_TINTS: Record<ModuleColor, ModuleTint> = {
  emerald: {
    iconBg: "bg-emerald-50 dark:bg-emerald-500/15",
    iconRing: "ring-emerald-200/70 dark:ring-emerald-500/25",
    iconText: "text-emerald-600 dark:text-emerald-400",
    watermark: "text-emerald-500",
  },
  sky: {
    iconBg: "bg-sky-50 dark:bg-sky-500/15",
    iconRing: "ring-sky-200/70 dark:ring-sky-500/25",
    iconText: "text-sky-600 dark:text-sky-400",
    watermark: "text-sky-500",
  },
  violet: {
    iconBg: "bg-violet-50 dark:bg-violet-500/15",
    iconRing: "ring-violet-200/70 dark:ring-violet-500/25",
    iconText: "text-violet-600 dark:text-violet-400",
    watermark: "text-violet-500",
  },
  rose: {
    iconBg: "bg-rose-50 dark:bg-rose-500/15",
    iconRing: "ring-rose-200/70 dark:ring-rose-500/25",
    iconText: "text-rose-600 dark:text-rose-400",
    watermark: "text-rose-500",
  },
  indigo: {
    iconBg: "bg-indigo-50 dark:bg-indigo-500/15",
    iconRing: "ring-indigo-200/70 dark:ring-indigo-500/25",
    iconText: "text-indigo-600 dark:text-indigo-400",
    watermark: "text-indigo-500",
  },
  amber: {
    iconBg: "bg-amber-50 dark:bg-amber-500/15",
    iconRing: "ring-amber-200/70 dark:ring-amber-500/25",
    iconText: "text-amber-600 dark:text-amber-400",
    watermark: "text-amber-500",
  },
  teal: {
    iconBg: "bg-teal-50 dark:bg-teal-500/15",
    iconRing: "ring-teal-200/70 dark:ring-teal-500/25",
    iconText: "text-teal-600 dark:text-teal-400",
    watermark: "text-teal-500",
  },
  slate: {
    iconBg: "bg-slate-50 dark:bg-white/[0.06]",
    iconRing: "ring-slate-200/70 dark:ring-white/15",
    iconText: "text-slate-600 dark:text-white/70",
    watermark: "text-slate-500",
  },
};

export function moduleTint(color: ModuleColor = "indigo"): ModuleTint {
  return MODULE_TINTS[color] ?? MODULE_TINTS.indigo;
}

export function FlagChip({
  countryCode,
  countryName,
}: {
  countryCode?: string | null;
  countryName?: string | null;
}) {
  const jur = resolveCatalogJurisdiction(countryCode, countryName);
  const [imgFailed, setImgFailed] = React.useState(false);
  const showGlobe = jur.code === "INTL" || imgFailed;
  return (
    <Tip label={jur.label}>
      <span className="inline-flex items-center justify-center">
        {showGlobe ? (
          <Globe className="size-4 text-sky-600 dark:text-sky-400" />
        ) : (
          <ReactCountryFlag
            countryCode={jur.code}
            svg
            title={jur.label}
            className="ring-1 ring-[#cbd5e1] dark:ring-white/20 shadow-sm"
            style={{ width: "1.4rem", height: "1rem", borderRadius: "3px", objectFit: "cover", display: "block" }}
            onError={() => setImgFailed(true)}
          />
        )}
      </span>
    </Tip>
  );
}

function CountryShape({
  geometry,
  className,
  style,
  strokeRgb,
}: {
  geometry: CountryGeometry;
  className?: string;
  style?: React.CSSProperties;
  strokeRgb: string;
}) {
  const shape = React.useMemo(() => {
    const { d, bbox } = geometryToPath(geometry);
    const span = Math.max(bbox.width, bbox.height) || 1;
    const pad = span * 0.08;
    const viewBox = `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`;
    const isWide = bbox.width >= bbox.height;
    return { d, viewBox, isWide };
  }, [geometry]);

  return (
    <svg
      aria-hidden
      viewBox={shape.viewBox}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{
        ...(shape.isWide
          ? { width: "54%", height: "auto", maxHeight: "64%" }
          : { height: "64%", width: "auto", maxWidth: "54%" }),
        ...style,
      }}
    >
      <path
        d={shape.d}
        fill="currentColor"
        fillRule="evenodd"
        stroke={`rgb(${strokeRgb})`}
        strokeWidth="1.5"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export function JurisdictionArt({
  countryCode,
  countryName,
}: {
  countryCode?: string | null;
  countryName?: string | null;
}) {
  const jur = resolveCatalogJurisdiction(countryCode, countryName);
  const [geometry, setGeometry] = React.useState<CountryGeometry | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    loadCountryGeometries().then((map) => {
      if (!cancelled) setGeometry(map.get(jur.code) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [jur.code]);

  if (!geometry) return null;
  const flagRgb = getJurisdictionAccentRgb(jur.code);
  return (
    <>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.11] dark:opacity-[0.22] z-0"
        style={{
          background: `linear-gradient(135deg, rgba(${flagRgb},1) 0%, rgba(${flagRgb},0.45) 42%, transparent 78%)`,
        }}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 size-52 rounded-full blur-3xl opacity-40 dark:opacity-50 z-0"
        style={{ background: `radial-gradient(circle, rgba(${flagRgb},0.55), transparent 70%)` }}
      />

      <CountryShape
        geometry={geometry}
        className="pointer-events-none absolute right-3 top-[54%] -translate-y-1/2 opacity-[0.16] dark:opacity-[0.26] z-0"
        style={{
          color: `rgb(${flagRgb})`,
          filter: `drop-shadow(0 0 18px rgba(${flagRgb},0.35))`,
        }}
        strokeRgb={flagRgb}
      />
    </>
  );
}

export interface CardAccent {

  rgb: string;

  onLight: string;

  onDark: string;
}

const flagCardAccentCache = new Map<string, CardAccent>();

export function catalogFlagAccent(
  countryCode?: string | null,
  countryName?: string | null,
): CardAccent | undefined {
  const rgb = catalogFlagAccentRgb(countryCode, countryName);
  if (!rgb) return undefined;
  const cached = flagCardAccentCache.get(rgb);
  if (cached) return cached;

  const accent: CardAccent = { rgb, ...accentTextVariants(rgb) };
  flagCardAccentCache.set(rgb, accent);
  return accent;
}

export function accentVars(accent: CardAccent): React.CSSProperties {
  return {
    "--acc-text": accent.onLight,
    "--acc-text-dark": accent.onDark,
    "--acc-bg": `rgba(${accent.rgb},0.10)`,
    "--acc-bg-dark": `rgba(${accent.rgb},0.16)`,
    "--acc-border": `rgba(${accent.rgb},0.30)`,
    "--acc-border-dark": `rgba(${accent.rgb},0.34)`,
  } as React.CSSProperties;
}

const ACCENT_CHIP_CLASSES =
  "bg-[var(--acc-bg)] dark:bg-[var(--acc-bg-dark)] text-[var(--acc-text)] dark:text-[var(--acc-text-dark)] border-[var(--acc-border)] dark:border-[var(--acc-border-dark)]";

export function VersionChip({
  version,
  tone = "light",
  accent,
}: {
  version: number | string;
  tone?: "light" | "dark";

  accent?: CardAccent;
}) {
  return (
    <span
      style={accent ? accentVars(accent) : undefined}
      className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] border text-[10px] font-bold tracking-widest whitespace-nowrap ${accent
        ? ACCENT_CHIP_CLASSES
        : tone === "dark"
          ? "bg-white/5 text-white/70 border-white/10"
          : "bg-[#f8fafc] text-[#475569] border-[#e2e8f0] dark:bg-white/[0.04] dark:text-white/70 dark:border-white/[0.08]"
        }`}
    >
      {formatVersion(version)}
    </span>
  );
}

export function TypeBadge({
  label,
  variant = "indigo",
  accent,
}: {
  label: string;
  variant?: "sky" | "indigo" | "violet";

  accent?: CardAccent;
}) {
  const c = accent
    ? ACCENT_CHIP_CLASSES
    : variant === "sky"
      ? "bg-sky-50 text-sky-700 border-transparent dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20"
      : variant === "violet"
        ? "bg-violet-50 text-violet-700 border-transparent dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/20"
        : "bg-indigo-50 text-indigo-700 border-transparent dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20";
  return (
    <span
      style={accent ? accentVars(accent) : undefined}
      className={`inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium border capitalize ${c}`}
    >
      {label}
    </span>
  );
}

export function TitleTooltip({
  text,
  children,
}: {
  text: string;
  children: React.ReactNode;
}) {
  if (!text) {
    return <span className="flex-1 min-w-0 block">{children}</span>;
  }
  return (
    <Tip
      label={text}
      // The trigger is the whole title row, so a centred bubble straddles it
      // and hangs past the card's left edge. Anchored to the title's start it
      // reads as a continuation of the text it explains.
      align="start"
      collisionPadding={12}
      className="flex-1 min-w-0 block"
      contentClassName="max-w-[280px] whitespace-normal break-words text-left leading-snug px-2 py-1 text-[10px]"
    >
      <span className="block min-w-0">{children}</span>
    </Tip>
  );
}

export function StatTile({
  icon,
  accent,
  value,
  label,
  tooltip,
  labelFirst,
  customSizes,
}: {
  icon: React.ReactNode;
  accent: "indigo" | "emerald" | "violet" | "sky";
  value: React.ReactNode;
  label: string;
  tooltip?: string;
  labelFirst?: boolean;
  customSizes?: { labelSize?: string; valueSize?: string };
}) {
  const STAT_TILE_TONES: Record<"indigo" | "emerald" | "violet" | "sky", string> = {
    indigo: "text-indigo-500 bg-indigo-50 ring-indigo-100 dark:bg-indigo-500/10 dark:ring-indigo-500/20",
    emerald: "text-emerald-500 bg-emerald-50 ring-emerald-100 dark:bg-emerald-500/10 dark:ring-emerald-500/20",
    violet: "text-violet-500 bg-violet-50 ring-violet-100 dark:bg-violet-500/10 dark:ring-violet-500/20",
    sky: "text-sky-500 bg-sky-50 ring-sky-100 dark:bg-sky-500/10 dark:ring-sky-500/20",
  };
  const tone = STAT_TILE_TONES[accent];
  const valueSize = customSizes?.valueSize ?? "15px";
  const labelSize = customSizes?.labelSize ?? "10px";
  const valueNode = (
    <span className="font-semibold text-foreground tabular-nums truncate block" style={{ fontSize: valueSize, lineHeight: "1.2" }}>
      {value}
    </span>
  );
  const labelNode = (
    <span className="font-semibold capitalize tracking-normal text-gray-900 dark:text-white whitespace-nowrap truncate" style={{ fontSize: labelSize, lineHeight: "1.2" }}>
      {label}
    </span>
  );
  return (
    <div className="flex flex-1 items-center gap-2 px-2.5 py-2 min-w-0">
      <span className={`flex size-6 flex-shrink-0 items-center justify-center rounded-full ring-1 ${tone}`}>
        {icon}
      </span>
      <div className="flex flex-col gap-0.5 leading-none min-w-0 flex-1">
        {labelFirst ? labelNode : (
          tooltip ? (
            <Tip
              label={tooltip}
              contentClassName="max-w-[200px] whitespace-normal break-words text-left leading-snug px-2 py-1 text-[10px]"
            >
              {valueNode}
            </Tip>
          ) : (
            valueNode
          )
        )}
        {labelFirst ? (
          tooltip ? (
            <Tip
              label={tooltip}
              contentClassName="max-w-[200px] whitespace-normal break-words text-left leading-snug px-2 py-1 text-[10px]"
            >
              {valueNode}
            </Tip>
          ) : (
            valueNode
          )
        ) : (
          labelNode
        )}
      </div>
    </div>
  );
}

export function StatTiles({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const arr = React.Children.toArray(children);
  return (
    <div className={`flex items-stretch rounded-xl border border-[#edf0f4] dark:border-white/[0.07] bg-[#f9fafb]/70 dark:bg-white/[0.02] ${className}`}>
      {arr.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <div className="w-px self-stretch my-2.5 bg-[#e6eaef] dark:bg-white/[0.08]" />
          )}
          {c}
        </React.Fragment>
      ))}
    </div>
  );
}

export function MetaDates({
  created,
  updated,
  createdLabel = "Created",
  updatedLabel = "Updated",
  accent,
}: {
  created: string;
  updated: string;
  createdLabel?: string;
  updatedLabel?: string;

  accent?: CardAccent;
}) {
  const iconClass = `size-3 flex-shrink-0 ${accent
    ? "text-[var(--acc-text)] dark:text-[var(--acc-text-dark)]"
    : "text-indigo-500 dark:text-indigo-400"
    }`;
  return (
    <div
      style={accent ? accentVars(accent) : undefined}
      className="flex flex-col gap-1 text-[10px] @[280px]:flex-row @[280px]:items-center @[280px]:gap-2.5 @[280px]:flex-wrap"
    >
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap break-normal">
        <Calendar className={iconClass} />
        <span className="font-semibold text-[#475569] dark:text-white/70">{createdLabel}</span>
        <span className="font-semibold text-[#0f172a] dark:text-white tabular-nums">
          {created}
        </span>
      </span>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap break-normal">
        <Clock className={iconClass} />
        <span className="font-semibold text-[#475569] dark:text-white/70">{updatedLabel}</span>
        <span className="font-semibold text-[#0f172a] dark:text-white tabular-nums">
          {updated}
        </span>
      </span>
    </div>
  );
}

export interface CardActionsProps {
  onExport?: () => void;
  onDelete?: () => void;
  exporting?: boolean;
  deleting?: boolean;
  canDelete?: boolean;
  deleteModule?: ModuleType;
  exportTooltip?: string;
  deleteTooltip?: string;
  deleteDisabledTooltip?: string;
}

export function CardActions({
  onExport,
  onDelete,
  exporting,
  deleting,
  canDelete = true,
  deleteModule = "catalog",
  exportTooltip = "Export",
  deleteTooltip = "Delete",
  deleteDisabledTooltip = "This item cannot be deleted",
}: CardActionsProps) {
  // One fixed 32px square per action, so every card's icons line up on the
  // same baseline and offer the same tap target whether one action is
  // rendered or both (RBAC can hide the delete). Only the hover colour
  // differs between them.
  const actionButton =
    "inline-flex size-8 flex-shrink-0 items-center justify-center rounded-lg cursor-pointer outline-none transition-colors text-[#64748b] dark:text-white/60 focus-visible:ring-2 focus-visible:ring-indigo-500/40 disabled:cursor-not-allowed disabled:hover:bg-transparent";
  return (
    <div className="flex h-8 items-center gap-1">
      {onExport && (
        <Tip label={exportTooltip}>
          <button
            type="button"
            aria-label={exportTooltip}
            onClick={(e) => {
              e.stopPropagation();
              onExport();
            }}
            disabled={!!exporting}
            className={`${actionButton} hover:bg-blue-50 dark:hover:bg-blue-500/10 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-40`}
          >
            {exporting ? <Loader2 className="size-4 animate-spin" /> : <SquareArrowOutUpRight className="size-4" />}
          </button>
        </Tip>
      )}
      {onDelete && (
        <RBACAction module={deleteModule} action="delete">
          <Tip label={canDelete ? deleteTooltip : deleteDisabledTooltip}>
            <button
              type="button"
              aria-label={canDelete ? deleteTooltip : deleteDisabledTooltip}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={!!deleting || !canDelete}
              className={`${actionButton} hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30`}
            >
              {deleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
            </button>
          </Tip>
        </RBACAction>
      )}
    </div>
  );
}

interface EntityCardProps<T> {
  item: T;
  getStatus: (item: T) => string;

  moduleIcon?: React.ReactNode | ((item: T) => React.ReactNode);

  moduleColor?: ModuleColor | ((item: T) => ModuleColor);

  showModuleWatermark?: boolean;
  renderTitle: (item: T) => React.ReactNode;
  renderHeaderRight?: (item: T) => React.ReactNode;
  renderBadges?: (item: T) => React.ReactNode;
  renderStats?: (item: T) => React.ReactNode;
  renderDates?: (item: T) => React.ReactNode;
  renderFooterActions?: (item: T) => React.ReactNode;

  renderCardDecoration?: (item: T) => React.ReactNode;

  decorationOverlapsEdge?: boolean;
  cardAccentVariant?: "subtle" | "bold";

  leadingIconShape?: "square" | "circle" | "none" | ((item: T) => "square" | "circle" | "none");

  showStatusAccentBar?: boolean;

  accentBarRgb?: (item: T) => string | undefined;

  hoverAccentRgb?: (item: T) => string | undefined;

  hoverAccentOnly?: boolean;

  getStatusLabel?: (item: T) => string;

  hideStatusBadge?: boolean;
  onOpen: (item: T) => void;
}

function EntityCard<T>({
  item,
  getStatus,
  moduleIcon,
  moduleColor = "indigo",
  showModuleWatermark = false,
  renderTitle,
  renderHeaderRight,
  renderBadges,
  renderStats,
  renderDates,
  renderFooterActions,
  renderCardDecoration,
  decorationOverlapsEdge = false,
  cardAccentVariant = "subtle",
  leadingIconShape = "square",
  showStatusAccentBar = false,
  accentBarRgb,
  hoverAccentRgb,
  hoverAccentOnly = false,
  getStatusLabel,
  hideStatusBadge = false,
  onOpen,
}: EntityCardProps<T>) {
  const status = getStatus(item);
  const accent = statusAccent(status);
  const resolvedIcon =
    typeof moduleIcon === "function"
      ? (moduleIcon as (item: T) => React.ReactNode)(item)
      : moduleIcon;
  const resolvedColor =
    typeof moduleColor === "function"
      ? (moduleColor as (item: T) => ModuleColor)(item)
      : moduleColor;
  const tint = moduleTint(resolvedColor);
  const resolvedIconShape =
    typeof leadingIconShape === "function"
      ? (leadingIconShape as (item: T) => "square" | "circle" | "none")(item)
      : leadingIconShape;
  const isBold = cardAccentVariant === "bold";
  const restClasses = isBold
    ? `border-2 ${accent.border} ${accent.bg}`
    : `border border-[#e2e8f0] dark:border-white/20 bg-card`;

const hoverRgb = hoverAccentRgb?.(item);
  const hoverVars = hoverRgb
    ? ({
        "--card-acc-border": `rgba(${hoverRgb},0.50)`,
        "--card-acc-border-dark": `rgba(${hoverRgb},0.60)`,
        "--card-acc-glow": `rgba(${hoverRgb},0.20)`,
        "--card-acc-glow-dark": `rgba(${hoverRgb},0.28)`,
        "--card-acc-sheen": `rgba(${hoverRgb},0.12)`,
        "--card-acc-sheen-dark": `rgba(${hoverRgb},0.18)`,
      } as React.CSSProperties)
    : undefined;

  const statusHover = !hoverRgb && !hoverAccentOnly;

  const hoverBorderOnGroup = hoverRgb
    ? "group-hover:border-[var(--card-acc-border)] dark:group-hover:border-[var(--card-acc-border-dark)]"
    : statusHover
      ? accent.hoverBorder.replace(/(?<!group-)hover:/g, "group-hover:")
      : "";
  const hoverGlowOnGroup = hoverRgb
    ? "group-hover:shadow-[0_8px_20px_-6px_var(--card-acc-glow)] dark:group-hover:shadow-[0_0_20px_var(--card-acc-glow-dark)]"
    : statusHover
      ? accent.hoverGlow.replace(
          /0_12px_28px_-8px/,
          "0_8px_20px_-6px",
        ).replace(/(?<!group-)hover:/g, "group-hover:")
      : "";
  const sheenVia = hoverRgb
    ? "via-[color:var(--card-acc-sheen)] dark:via-[color:var(--card-acc-sheen-dark)]"
    : statusHover
      ? accent.sheenVia
      : null;
  const footerless = hideStatusBadge && !renderFooterActions;
  return (
    <div
      onClick={() => onOpen(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      className="group relative h-full rounded-2xl cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
    >
    <div
      style={hoverVars}
      className={`relative flex h-full flex-col rounded-2xl ${restClasses} overflow-hidden transition-all duration-200 ease-out group-hover:-translate-y-0.5 ${hoverBorderOnGroup} ${hoverGlowOnGroup}`}
    >
      {renderCardDecoration && renderCardDecoration(item)}
      {isBold && <StatusGlow accent={accent} />}
      {showStatusAccentBar && (() => {
        const barRgb = accentBarRgb?.(item);
        return barRgb ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1 z-[1]"
            style={{ backgroundColor: `rgba(${barRgb},0.55)` }}
          />
        ) : (

          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 bottom-0 h-1 z-[1] ${hoverAccentOnly ? "bg-slate-300 dark:bg-white/25" : accent.bar
              }`}
          />
        );
      })()}
      {showModuleWatermark && resolvedIcon && (
        <span
          aria-hidden
          className={`pointer-events-none absolute -bottom-3 -right-3 size-20 opacity-[0.07] dark:opacity-[0.12] ${tint.watermark} z-0`}
        >
          {React.isValidElement(resolvedIcon)
            ? React.cloneElement(resolvedIcon as React.ReactElement<{ className?: string; strokeWidth?: number }>, {
                className: "size-full",
                strokeWidth: 1.25,
              })
            : resolvedIcon}
        </span>
      )}
      {sheenVia && (
        <span
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent ${sheenVia} to-transparent group-hover:translate-x-[220%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out z-[1]`}
        />
      )}
      <div
        className={`@container relative z-[2] flex flex-1 flex-col gap-3 p-3.5 pl-4 ${decorationOverlapsEdge ? "pt-6" : ""} ${footerless ? "min-h-[180px]" : ""}`}
      >

        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {resolvedIcon && (
              <span
                className={
                  resolvedIconShape === "none"
                    ? "flex size-9 flex-shrink-0 items-center justify-center"
                    : `flex size-9 flex-shrink-0 items-center justify-center ${resolvedIconShape === "circle" ? "rounded-full" : "rounded-xl"} ring-1 ${tint.iconBg} ${tint.iconRing} ${tint.iconText}`
                }
              >
                {resolvedIconShape === "none"
                  ? resolvedIcon
                  : React.isValidElement(resolvedIcon)
                    ? React.cloneElement(resolvedIcon as React.ReactElement<{ className?: string }>, {
                        className: "size-4",
                      })
                    : resolvedIcon}
              </span>
            )}
            <div className="min-w-0 flex-1">{renderTitle(item)}</div>
          </div>
          {renderHeaderRight && <div className="shrink-0">{renderHeaderRight(item)}</div>}
        </div>
        {renderBadges && (
          <div className="flex items-center gap-1.5 flex-wrap">{renderBadges(item)}</div>
        )}
        <div className="mt-auto flex flex-col gap-3">
          {renderStats && renderStats(item)}
          {renderDates && renderDates(item)}
          {(!hideStatusBadge || renderFooterActions) && (
            <div
              className={`flex items-center gap-2 pt-3 border-t border-[#eef2f6] dark:border-white/[0.06] ${hideStatusBadge ? "justify-end" : "justify-between"}`}
            >
              {!hideStatusBadge && (
                <StatusBadge
                  status={status as Parameters<typeof StatusBadge>[0]["status"]}
                  label={getStatusLabel ? getStatusLabel(item) : undefined}
                  dot
                />
              )}
              <div className="flex-shrink-0">
                {renderFooterActions && renderFooterActions(item)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

function pageWindow(current: number, total: number): (number | "dots")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "dots", total];
  if (current >= total - 3) return [1, "dots", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "dots", current - 1, current, current + 1, "dots", total];
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (p: number) => void;
  itemLabel?: string;
}

export function Pagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  itemLabel = "items",
}: PaginationProps) {
  const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1;
  if (totalItems === 0) return null;
  return (
    <div className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015]">
      <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
        Showing{" "}
        <span className="font-semibold text-[#334155] dark:text-white/90">
          {page * pageSize + 1}&ndash;{Math.min((page + 1) * pageSize, totalItems)}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-[#334155] dark:text-white/90">{totalItems}</span>{" "}
        {itemLabel}
      </p>
      {totalPages > 1 && (
        <div className="flex items-center gap-1.5">
          <button
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            <ChevronLeft className="size-4" />
          </button>
          {pageWindow(page + 1, totalPages).map((p, idx) =>
            p === "dots" ? (
              <span
                key={`dots-${idx}`}
                className="flex size-8 items-center justify-center text-xs text-[#94a3b8] dark:text-white/40 select-none"
              >
                &hellip;
              </span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p - 1)}
                className={`flex size-8 items-center justify-center text-xs font-semibold rounded-lg border transition-colors ${p - 1 === page
                  ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                  : "border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15"
                  }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(page + 1)}
            className="flex size-8 items-center justify-center rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] hover:border-[#cbd5e1] dark:hover:border-white/15 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export interface RowsPerCardSelectProps {
  value: number;
  onChange: (n: number) => void;
  options?: number[];
  label?: string;

  max?: number;
}

export function RowsPerCardSelect({
  value,
  onChange,
  options = [10, 25, 50, 100],
  label = "Items per page",
  max = 500,
}: RowsPerCardSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value));
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setDraft(String(value));
  }, [value]);

  React.useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const commit = (raw: string) => {
    const n = Math.floor(Number(raw));
    if (!raw.trim() || !Number.isFinite(n) || n < 1) {
      setDraft(String(value));
      return;
    }
    const clamped = Math.min(n, max);
    setDraft(String(clamped));
    if (clamped !== value) onChange(clamped);
  };

  const pick = (n: number) => {
    setDraft(String(n));
    setOpen(false);
    if (n !== value) onChange(n);
  };

  return (
    <div
      ref={wrapRef}
      className="flex items-center gap-2 flex-shrink-0"
    >
      <label className="text-sm d whitespace-nowrap">{label}</label>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={draft}
          aria-label={label}
          maxLength={String(max).length}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commit(draft);
              setOpen(false);
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Escape") {
              setDraft(String(value));
              setOpen(false);
            }
          }}
          // h-[38px] matches the search box and the filter dropdowns, so the
          // whole toolbar row sits on one line.
          className="h-[38px] w-[78px] pl-2.5 pr-7 text-sm font-medium tabular-nums bg-card border border-[#e2e8f0] dark:border-white/[0.08] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all"
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={`${label} suggestions`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="absolute right-1 top-1/2 -translate-y-1/2 flex size-5 items-center justify-center rounded text-[#64748b] dark:text-white/60 hover:text-foreground transition-colors"
        >
          <ChevronDown
            className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 z-30 min-w-[78px] max-h-56 overflow-y-auto rounded-lg border border-[#e2e8f0] dark:border-white/[0.08] bg-card py-1 shadow-lg">
            {options.map((n) => (
              <button
                key={n}
                type="button"

                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(n)}
                className={`block w-full px-3 py-1.5 text-left text-sm tabular-nums transition-colors ${n === value
                  ? "font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10"
                  : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05]"
                  }`}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export interface EntityCardGridProps<T> {
  items: T[];
  loading?: boolean;

  getKey?: (item: T) => string;

  getStatus: (item: T) => string;

  moduleIcon?: React.ReactNode | ((item: T) => React.ReactNode);

  moduleColor?: ModuleColor | ((item: T) => ModuleColor);

  showModuleWatermark?: boolean;

  renderTitle: (item: T) => React.ReactNode;

  renderHeaderRight?: (item: T) => React.ReactNode;

  renderBadges?: (item: T) => React.ReactNode;

  renderStats?: (item: T) => React.ReactNode;

  renderDates?: (item: T) => React.ReactNode;

  renderFooterActions?: (item: T) => React.ReactNode;

  renderCardDecoration?: (item: T) => React.ReactNode;

  decorationOverlapsEdge?: boolean;

  cardAccentVariant?: "subtle" | "bold";

  leadingIconShape?: "square" | "circle" | "none" | ((item: T) => "square" | "circle" | "none");

  showStatusAccentBar?: boolean;

  accentBarRgb?: (item: T) => string | undefined;

  hoverAccentRgb?: (item: T) => string | undefined;

  hoverAccentOnly?: boolean;

  getStatusLabel?: (item: T) => string;

  hideStatusBadge?: boolean;

  onOpen: (item: T) => void;

  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (p: number) => void;

  onPageSizeChange?: (s: number) => void;
  pageSizeOptions?: number[];
  itemLabel?: string;

  toolbarLeft?: React.ReactNode;

  emptyIcon?: React.ReactNode;
  emptyText?: string;

  skeletonHeight?: number;

  renderTable?: (items: T[]) => React.ReactNode;
  serverSideTable?: boolean;

  hideBuiltInControls?: boolean;

  defaultViewMode?: "card" | "table";

  viewMode?: "card" | "table";
  onViewModeChange?: (mode: "card" | "table") => void;

  gridCols?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    "2xl"?: number;
  };
  gridGap?: number | { base?: number; sm?: number; md?: number; lg?: number; xl?: number; "2xl"?: number };
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: "card" | "table";
  onChange: (v: "card" | "table") => void;
}) {
  const btn = (mode: "card" | "table", label: string, icon: React.ReactNode) => {
    const active = value === mode;
    return (
      <Tip label={label}>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={() => onChange(mode)}
          className={`flex size-8 items-center justify-center rounded-md transition-colors ${active
            ? "bg-card text-indigo-600 dark:text-indigo-400 shadow-sm"
            : "text-[#64748b] dark:text-white/60 hover:text-foreground"
            }`}
        >
          {icon}
        </button>
      </Tip>
    );
  };
  return (
    // Same 38px height as the rest of the toolbar controls.
    <div className="inline-flex h-[38px] items-center gap-0.5 px-1 rounded-lg bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
      {btn("card", "Card view", <LayoutGrid className="size-4" />)}
      {btn("table", "Table view", <List className="size-4" />)}
    </div>
  );
}

export function EntityCardGrid<T>(props: EntityCardGridProps<T>) {
  const {
    items,
    loading,
    getKey,
    getStatus,
    moduleIcon,
    moduleColor,
    showModuleWatermark,
    renderTitle,
    renderHeaderRight,
    renderBadges,
    renderStats,
    renderDates,
    renderFooterActions,
    renderCardDecoration,
    decorationOverlapsEdge,
    cardAccentVariant,
    leadingIconShape,
    showStatusAccentBar,
    accentBarRgb,
    hoverAccentRgb,
    hoverAccentOnly,
    getStatusLabel,
    hideStatusBadge,
    onOpen,
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions,
    itemLabel = "items",
    toolbarLeft,
    emptyIcon,
    emptyText = "No items found",
    skeletonHeight = 260,
    renderTable,
    serverSideTable = false,
    hideBuiltInControls = false,
    defaultViewMode = "card",
    viewMode: controlledViewMode,
    onViewModeChange,
    gridCols,
    gridGap,
  } = props;

  const keyOf = getKey || ((it: T) => (it as { id?: string }).id ?? "");
  const [internalViewMode, setInternalViewMode] = React.useState<"card" | "table">(
    defaultViewMode,
  );
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = (mode: "card" | "table") => {
    onViewModeChange?.(mode);
    if (controlledViewMode === undefined) setInternalViewMode(mode);
  };
  const canToggle = !!renderTable;
  const activeView: "card" | "table" = canToggle ? viewMode : "card";

  const showBuiltInControls = !hideBuiltInControls;
  const hasToolbar =
    !!toolbarLeft || (showBuiltInControls && (!!onPageSizeChange || canToggle));

  // Build responsive grid classes from config (8px-based spacing system)
  const buildGridClasses = (): string => {
    const cols = gridCols ?? { base: 1, sm: 2, lg: 3, xl: 4 };
    const gap = gridGap ?? 2; // default to 8px (gap-2)

    const colClasses = [
      cols.base && `grid-cols-${cols.base}`,
      cols.sm && `sm:grid-cols-${cols.sm}`,
      cols.md && `md:grid-cols-${cols.md}`,
      cols.lg && `lg:grid-cols-${cols.lg}`,
      cols.xl && `xl:grid-cols-${cols.xl}`,
      cols["2xl"] && `2xl:grid-cols-${cols["2xl"]}`,
    ].filter(Boolean).join(" ");

    const gapValue = typeof gap === "number" ? gap : gap.base ?? 2;
    const gapClasses = [
      `gap-${gapValue}`,
      gap.sm && `sm:gap-${gap.sm}`,
      gap.md && `md:gap-${gap.md}`,
      gap.lg && `lg:gap-${gap.lg}`,
      gap.xl && `xl:gap-${gap.xl}`,
      gap["2xl"] && `2xl:gap-${gap["2xl"]}`,
    ].filter(Boolean).join(" ");

    return `${colClasses} ${gapClasses}`;
  };

  const gridClasses = buildGridClasses();

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {hasToolbar && (
        <div className="flex-shrink-0 flex items-center gap-3 flex-wrap">
          {toolbarLeft}
          {showBuiltInControls && (
          <div className="ml-auto flex items-center gap-3 flex-shrink-0">
            {canToggle && (
              <ViewModeToggle value={activeView} onChange={setViewMode} />
            )}
            {onPageSizeChange && (
              <RowsPerCardSelect
                value={pageSize}
                onChange={onPageSizeChange}

                options={
                  pageSizeOptions ??
                  (activeView === "table"
                    ? [10, 25, 50, 100]
                    : [12, 24, 48, 96])
                }
                label={
                  activeView === "table" ? "Rows per page" : "Cards per page"
                }
              />
            )}
          </div>
          )}
        </div>
      )}

      <div
        className={
          activeView === "table"
            ? "min-h-0 flex flex-col"
            : "flex-1 min-h-0 overflow-y-auto pr-1 -mr-1"
        }
      >
        {loading ? (
          activeView === "table" ? (
            <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-card overflow-hidden">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse h-12 border-b border-[#eef2f6] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]"
                />
              ))}
            </div>
          ) : (
            <div className={`grid ${gridClasses}`}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-2xl border-2 border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-white/[0.03] p-3.5 flex flex-col gap-3"
                  style={{ minHeight: skeletonHeight }}
                >
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div className="size-9 rounded-xl bg-[#e2e8f0] dark:bg-white/[0.09] flex-shrink-0" />
                      <div className="h-3.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-3/4" />
                    </div>
                    <div className="size-4 rounded bg-[#e2e8f0] dark:bg-white/[0.09]" />
                  </div>
                  <div className="flex gap-1.5">
                    <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-16" />
                    <div className="h-4 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09] w-10" />
                  </div>
                  <div className="mt-auto flex flex-col gap-3">
                    <div className="h-14 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.05] border border-[#edf0f4] dark:border-white/[0.07]" />
                    <div className="flex flex-col gap-1.5">
                      <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-2/3" />
                      <div className="h-2.5 rounded bg-[#e2e8f0] dark:bg-white/[0.09] w-1/2" />
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                      <div className="h-4 rounded-full bg-[#e2e8f0] dark:bg-white/[0.09] w-20" />
                      <div className="flex gap-1">
                        <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                        <div className="size-6 rounded-md bg-[#e2e8f0] dark:bg-white/[0.09]" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : items.length === 0 &&
          !(serverSideTable && activeView === "table" && renderTable) ? (
          <div className="flex flex-col items-center justify-center gap-2 py-20 rounded-2xl border border-dashed border-[#e2e8f0] dark:border-white/[0.08]">
            {emptyIcon ?? <FileText className="size-8 text-[#cbd5e1] dark:text-white/30" />}
            <p className="text-sm text-[#94a3b8] dark:text-white/50">{emptyText}</p>
          </div>
        ) : activeView === "table" && renderTable ? (
          renderTable(items)
        ) : (
          <div className={`grid ${gridClasses} pt-2 px-1`}>
            {items.map((item) => (
              <EntityCard
                key={keyOf(item)}
                item={item}
                getStatus={getStatus}
                moduleIcon={moduleIcon}
                moduleColor={moduleColor}
                showModuleWatermark={showModuleWatermark}
                renderTitle={renderTitle}
                renderHeaderRight={renderHeaderRight}
                renderBadges={renderBadges}
                renderStats={renderStats}
                renderDates={renderDates}
                renderFooterActions={renderFooterActions}
                renderCardDecoration={renderCardDecoration}
                decorationOverlapsEdge={decorationOverlapsEdge}
                cardAccentVariant={cardAccentVariant}
                leadingIconShape={leadingIconShape}
                showStatusAccentBar={showStatusAccentBar}
                accentBarRgb={accentBarRgb}
                hoverAccentRgb={hoverAccentRgb}
                hoverAccentOnly={hoverAccentOnly}
                getStatusLabel={getStatusLabel}
                hideStatusBadge={hideStatusBadge}
                onOpen={onOpen}
              />
            ))}
          </div>
        )}
      </div>

      {!loading &&
        totalItems > 0 &&
        !(serverSideTable && activeView === "table" && renderTable) && (
        <div className="flex-shrink-0 mb-1">
          <Pagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
            itemLabel={itemLabel}
          />
        </div>
      )}
    </div>
  );
}
